/**
 * Contact Page
 * 
 * Contact form and information for CHEKSTE
 */

import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import { ArrowLeft, Mail, MessageSquare, Send, Loader2, CheckCircle2, HelpCircle, Bug, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

// Privacy requests (access, correction, deletion) arrive through this same form as a
// general inquiry — the Privacy Policy (§23) names chekste.com/contact as the channel —
// and are handled by hand. There is no dedicated subject for them any more: one existed,
// with a pre-written message, and it promised a process that did not exist behind it.
const KNOWN_SUBJECTS = ['general', 'support', 'feedback', 'billing'];

const Contact = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requested = searchParams.get('subject');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: KNOWN_SUBJECTS.includes(requested) ? requested : 'general',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const subjects = [
    { value: 'general', label: 'General Inquiry', icon: HelpCircle },
    { value: 'support', label: 'Technical Support', icon: Bug },
    { value: 'feedback', label: 'Feedback & Suggestions', icon: Lightbulb },
    { value: 'billing', label: 'Billing Question', icon: Mail },
  ];
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Send real email via backend
      const response = await api.post('/api/contact/submit', {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message
      });
      
      if (response.data.success) {
        setSubmitted(true);
        toast.success('Message sent successfully!');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to send message. Please try email directly.';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };
  
  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        
        <div className="container mx-auto px-4 sm:px-6 py-8 max-w-2xl">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Message Sent!</h2>
            <p className="text-slate-600 mb-6">
              Thank you for reaching out. We aim to respond within 1–2 business days.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => {
                  setSubmitted(false);
                  setFormData({ name: '', email: '', subject: 'general', message: '' });
                }}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Send Another
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 sm:px-6 py-8 max-w-4xl">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-800 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        
        <div className="grid md:grid-cols-5 gap-8">
          {/* Contact Info */}
          <div className="md:col-span-2">
            <h1 className="text-3xl font-bold text-slate-900 mb-4">Contact Us</h1>
            <p className="text-slate-600 mb-8">
              Have a question, need help, or want to share feedback? We're here to help.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-medium text-slate-800">Get in Touch</h3>
                  {/* The old support mailbox sat on a retired domain: it invited a
                      message that could never arrive. The form below is the channel that
                      actually works, so point at it. */}
                  <p className="text-slate-600 text-sm">
                    For all inquiries — technical support, billing questions, feedback, or general
                    questions — use the form below and we will reply by email.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-medium text-slate-800">Response Time</h3>
                  <p className="text-slate-600">We aim to respond within 1–2 business days</p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h3 className="font-medium text-slate-800 mb-2">What We Can Help With</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li><strong className="text-slate-700">Technical support</strong> — issues with the app, analyses, or your account</li>
                <li><strong className="text-slate-700">Billing</strong> — questions about your subscription, plan, or cancellation</li>
                <li><strong className="text-slate-700">Feedback</strong> — suggestions to make CHEKSTE better</li>
                <li><strong className="text-slate-700">General questions</strong> — anything else about the Service</li>
              </ul>
            </div>

            <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h3 className="font-medium text-slate-800 mb-2">About CHEKSTE</h3>
              <p className="text-sm text-slate-600">
                CHEKSTE is operated by Digital Solutions Consulting LLC (DIGISCON), a Michigan-based
                company providing real estate deal analysis tools.
              </p>
            </div>

            <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h3 className="font-medium text-slate-800 mb-2">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/terms" className="text-indigo-600 hover:text-indigo-700">Terms of Service</Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-indigo-600 hover:text-indigo-700">Privacy Policy</Link>
                </li>
                <li>
                  <Link to="/pricing" className="text-indigo-600 hover:text-indigo-700">Pricing</Link>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Contact Form */}
          <div className="md:col-span-3">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Your name"
                    data-testid="contact-name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="your@email.com"
                    data-testid="contact-email"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Subject
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {subjects.map(({ value, label, icon: Icon }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setFormData({ ...formData, subject: value })}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                          formData.subject === value
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                        data-testid={`subject-${value}`}
                      >
                        <Icon className="w-4 h-4" />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={5}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                    placeholder="How can we help you?"
                    data-testid="contact-message"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
